/**
 * Configuração do app, validada na inicialização.
 *
 * Mesmo princípio do `config.py` do back: falhar no boot por variável
 * ausente é preferível a descobrir no primeiro request.
 */
import { z } from 'zod';

/** Provedores de login social que a tela conhece (alias do IdP no Keycloak). */
export const SOCIAL_PROVIDERS = ['google', 'apple'] as const;
export type SocialProvider = (typeof SOCIAL_PROVIDERS)[number];

/**
 * `VITE_SOCIAL_LOGIN=google,apple` → provedores já conectados no Keycloak.
 * Ignora maiúsculas, espaços, repetidos e nomes desconhecidos.
 */
export function parseSocialProviders(raw: string | undefined): SocialProvider[] {
  const wanted = new Set((raw ?? '').split(',').map((p) => p.trim().toLowerCase()));
  return SOCIAL_PROVIDERS.filter((p) => wanted.has(p));
}

/** "true"/"1"/"yes"/"on" ligam; qualquer outra coisa (inclusive vazio) desliga. */
export function parseFlag(raw: string | undefined): boolean {
  return /^(1|true|yes|on)$/i.test((raw ?? '').trim());
}

const schema = z.object({
  VITE_API_URL: z.string().default(''),
  VITE_KEYCLOAK_URL: z.url().default('http://localhost:8081'),
  VITE_KEYCLOAK_REALM: z.string().min(1).default('centralone'),
  VITE_KEYCLOAK_CLIENT_ID: z.string().min(1).default('centralone-spa'),
  // Login social: só os listados redirecionam; os outros botões aparecem,
  // mas avisam que ainda não estão disponíveis. Ver infra/keycloak/README.md.
  VITE_SOCIAL_LOGIN: z.string().default(''),
  // "Esqueceu a senha?" por e-mail — só com o SMTP do Keycloak configurado.
  VITE_PASSWORD_RESET_EMAIL: z.string().default(''),
});

const env = schema.parse(import.meta.env);

export const config = {
  apiUrl: env.VITE_API_URL.replace(/\/$/, ''),
  apiPrefix: '/v1',
  keycloak: {
    url: env.VITE_KEYCLOAK_URL.replace(/\/$/, ''),
    realm: env.VITE_KEYCLOAK_REALM,
    clientId: env.VITE_KEYCLOAK_CLIENT_ID,
  },
  auth: {
    socialProviders: parseSocialProviders(env.VITE_SOCIAL_LOGIN),
    passwordResetByEmail: parseFlag(env.VITE_PASSWORD_RESET_EMAIL),
  },
  isDev: import.meta.env.DEV,
} as const;
